import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { queryClient } from '../constants.js';
import { commonQueryProps } from './cache.js';
import type { ConstraintInfo, ProcessedConstraintMetadata } from '../../types/metadata.js';
import type { CachedQueryResult } from '../../types/supabase.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

/**
 * Builds the Supabase query for fetching constraint metadata
 * @returns PostgrestBuilder query for constraint_info with all necessary columns
 */
export const buildQueryForConstraints = () => {
  return supabaseCoreClient
    .from('constraint_info')
    .select('*');
};

/**
 * Post-processes constraint data by grouping by table and constraint name
 * @param data - Raw constraint data from database
 * @returns Processed constraint metadata grouped by table
 */
export const postProcessConstraints = (data: ConstraintInfo[]): ProcessedConstraintMetadata => {
  const groupedByTable: ProcessedConstraintMetadata = {
    flattened: {},
    byId: {},
    byConstraintName: {},
    byTableName: {},
    asList: data
  };

  data.forEach(constraint => {
    const tableName = constraint.table_name;
    if (!groupedByTable.byTableName[tableName]) {
      groupedByTable.byTableName[tableName] = { asList: [], byConstraintName: {}, byRelevantField: {} };
    }
    groupedByTable.byTableName[tableName].asList.push(constraint);
    groupedByTable.byTableName[tableName].byConstraintName[constraint.constraint_name] = constraint;
    if (constraint.relevant_field) {
      groupedByTable.byTableName[tableName].byRelevantField[constraint.relevant_field] = constraint;
    }
    groupedByTable.flattened[`${constraint.table_name}_${constraint.id}`] = constraint;
    groupedByTable.byId[constraint.id] = constraint;
    groupedByTable.byConstraintName[constraint.constraint_name] = constraint;
    
    // Also add table-level access for backward compatibility
    if (!(groupedByTable as any)[tableName]) {
      (groupedByTable as any)[tableName] = groupedByTable.byTableName[tableName];
    }
  });

  return groupedByTable;
};

// Result type for constraint operations
interface ConstraintResult {
  data: ProcessedConstraintMetadata | null;
  error: any;
}

/**
 * Fetches constraints data outside of React context
 * @returns Promise with processed constraint metadata or error
 */
export const getConstraints = async (): Promise<ConstraintResult> => {
  const query = buildQueryForConstraints();
  let constraintData: ConstraintInfo[] | null = null;
  let constraintError: any = null;
  
  try {
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(query, false)) as CachedQueryResult<ConstraintInfo[]>;
    constraintData = cachedResult?.data ?? null;
  } catch {
    // Cache miss or error, will fetch from API
  }
  
  if (!constraintData) {
    const { data, error } = await query;
    constraintData = data;
    constraintError = error;
  }

  if (constraintError) {
    return { data: null, error: constraintError };
  }

  const processedData = postProcessConstraints(constraintData ?? []);
  return { data: processedData, error: null };
};

// Hook options interface
interface UseConstraintsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseConstraintsResult {
  data: ProcessedConstraintMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch constraints data using React Query
 * @param options - Options including enabled flag
 * @returns Constraint data with loading and error states
 */
export const useConstraints = ({ enabled }: UseConstraintsOptions): UseConstraintsResult => {
  const bootReady = useMetadataBootReady();
  const query = buildQueryForConstraints();

  const { data, error, isPending } = useQuery(
    query,
    {
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.constraint_info')
    }
  );

  // Memoized on the query result so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(() => (data ? postProcessConstraints(data) : null), [data]);

  if (isPending || error || !data) {
    return {
      data: null,
      error,
      isLoading: isPending
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};
